import * as React from 'react';

export interface TotpCodeInputProps {
  value: string;
  onChange: (next: string) => void;
  onComplete?: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: string | null;
  length?: number;
  className?: string;
  ariaLabel?: string;
}

export const TotpCodeInput: React.FC<TotpCodeInputProps> = ({
  value,
  onChange,
  onComplete,
  autoFocus = false,
  disabled = false,
  error = null,
  length = 6,
  className,
  ariaLabel,
}) => {
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);

  React.useEffect(() => {
    if (autoFocus) {
      refs.current[0]?.focus();
    }
  }, [autoFocus]);

  const digits = React.useMemo<string[]>(() => {
    const padded = value.padEnd(length, ' ').slice(0, length);
    return padded.split('').map((c) => (c === ' ' ? '' : c));
  }, [value, length]);

  const setDigit = (index: number, raw: string): void => {
    if (!/^\d*$/.test(raw)) return;
    const next = [...digits];
    next[index] = raw.slice(-1);
    const joined = next.join('');
    onChange(joined);
    if (raw && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
    if (joined.replace(/\s/g, '').length === length && onComplete) {
      onComplete(joined);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.padEnd(length, '').split('').slice(0, length);
    while (next.length < length) next.push('');
    onChange(next.join(''));
    const nextEmpty = pasted.length < length ? pasted.length : length - 1;
    refs.current[nextEmpty]?.focus();
    if (pasted.length === length && onComplete) {
      onComplete(pasted);
    }
  };

  return (
    <div className={className}>
      <div
        className="flex items-center justify-between gap-2"
        role="group"
        aria-label={ariaLabel ?? 'Codice TOTP a 6 cifre'}
      >
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={d}
            disabled={disabled}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            aria-label={`Cifra ${i + 1} di ${length}`}
            className={`h-12 w-10 sm:h-14 sm:w-12 text-center text-xl font-mono rounded-lg border bg-background text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500 ${
              error
                ? 'border-red-400 dark:border-red-500/60'
                : 'border-border dark:border-slate-700'
            } disabled:opacity-50`}
          />
        ))}
      </div>
      {error && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
